import React, { useState, useEffect } from 'react'
import { Tabs, Tab } from 'transfix-ui/components/DeprecatedTabs'
import { Box } from 'transfix-ui/core'
import makeStyles from 'transfix-ui/core/styles/makeStyles'
import { brandColors } from 'transfix-ui/theme'
import { JsonParam, useQueryParam } from 'use-query-params'
import { InlineMessage } from 'transfix-ui'
import { SpotQuoteToolContainer, TABS } from '../../components/SpotQuoteToolContainer'
import { defaultQuoteFilters } from '../constants/quoteFilters'
import { useHistoricalQuotes } from '../hooks/useHistoricalQuotes'
import { useQuoteSortingOptions } from '../hooks/useQuoteSortingOptions'
import { TQuoteFilters } from '../types'
import { getDateLimitForQuotes } from '../utils/formatHistoricalQuotesTableContents'
import { HistoricalQuotesTable } from './HistoricalQuotesTable'
import { HistoricalQuotesFilters } from './filters/HistoricalQuotesFilters'

export const QUOTES_PER_PAGE = 50

const useStyles = makeStyles(theme => ({
  note: {
    fontWeight: 'bold',
  },
  tab: {
    height: '1904px',
    backgroundColor: brandColors.coolGray0,
  },
  toggle: {
    padding: theme.spacing(2),
  },
  tabMargin: {
    marginBottom: `${theme.spacing(0)} !important`,
  },
  spotQuoteToggle: {
    '& > label': {
      marginLeft: 0,
      paddingBottom: theme.spacing(3),
    },
  },
}))

export const HistoricalQuotes = () => {
  const classes = useStyles()
  const changeTabHandler = (newTab: TABS) => {
    if (newTab === TABS.generateQuote) {
      window.open(`${__ROOT__}/spot-quote-tool`, '_self')
    }
  }

  const [focusedQuoteId, setFocusedQuoteId] = useState<string | null>(null)

  const [filterParam, setFilterParam] = useQueryParam('filters', JsonParam)
  const initialQuoteFilters = {
    ...defaultQuoteFilters,
    ...filterParam,
  }
  const [quotesFilters, setQuotesFilters] = useState<TQuoteFilters>(initialQuoteFilters)

  // Update the filters query param whenever the
  // filter state has changed:
  useEffect(() => {
    setFilterParam(quotesFilters)
  }, [quotesFilters])

  const {
    defaultOptionLabel,
    onSortingOptionSelect,
    sortBy,
    sortDirection,
    sortingOptions,
  } = useQuoteSortingOptions({ setFocusedQuoteId })

  const {
    currentPage,
    handlePageChange,
    loading: priceRequestsLoading,
    paginatedPriceRequests,
    totalCount,
    totalPages,
  } = useHistoricalQuotes({
    quotesPerPage: QUOTES_PER_PAGE,
    sortBy,
    sortDirection,
    quotesFilters,
    setFocusedQuoteId,
  })

  const dateLimit = getDateLimitForQuotes(quotesFilters)

  const displayTotalCount = totalCount.toLocaleString()

  return (
    <SpotQuoteToolContainer>
      <Tabs
        tabListClassName={classes.tabMargin}
        value={TABS.searchQuotes}
        onChange={changeTabHandler}
        dataTest='spot-quote-tool-tabs'>
        <Tab label={TABS.generateQuote} key={TABS.generateQuote}>
          <></>
        </Tab>
        <Tab label={TABS.searchQuotes} key={TABS.searchQuotes}>
          <Box ml={2} mr={2} mt={2}>
            <Box mb={1.5}>
              <HistoricalQuotesFilters
                quotesFilters={quotesFilters}
                numberOfPriceRequests={paginatedPriceRequests.length}
                displayTotalCount={displayTotalCount}
                dateLimit={dateLimit}
                setQuotesFilters={setQuotesFilters}
                setFocusedQuoteId={setFocusedQuoteId}
                sortBy={sortBy}
                sortDirection={sortDirection}
                defaultOptionLabel={defaultOptionLabel}
                onSortingOptionSelect={onSortingOptionSelect}
                sortingOptions={sortingOptions}
              />
            </Box>
            {(quotesFilters.pickupRange || quotesFilters.deliveryRange) && (
              <InlineMessage
                message={
                  <span>
                    <span className={classes.note}>Note: </span>
                    Currently, pickup/delivery range is an approximation i.e. some locations that
                    display in this filter may not be exactly in the range
                  </span>
                }
              />
            )}
            <HistoricalQuotesTable
              priceRequests={paginatedPriceRequests}
              quotesPerPage={QUOTES_PER_PAGE}
              currentPage={currentPage}
              totalPages={totalPages}
              handlePageChange={handlePageChange}
              totalCount={totalCount}
              priceRequestsLoading={priceRequestsLoading}
              focusedQuoteId={focusedQuoteId}
              setFocusedQuoteId={setFocusedQuoteId}
            />
          </Box>
        </Tab>
      </Tabs>
    </SpotQuoteToolContainer>
  )
}
